import {Suspense, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {raisedSurfaceShadow} from '../../../styles/elevation';
import {ConnectionHandler} from 'relay-runtime';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import {useCreateTicket} from '../hooks/useCreateTicket';
import {useMentionUsers} from '../hooks/useMentionUsers';
import {AssigneePicker} from './AssigneePicker';
import {TicketLabelPicker} from './TicketLabelPicker';
import {ProjectDropdown} from './ProjectDropdown';
import type {TicketPriority} from '../types/ticket';

interface CreateTicketModalProps {
	open: boolean;
	onClose: () => void;
	onCreated?: (ticket: {id: string; slug: string}) => void;
	/**
	 * The project in context where the ticket is raised from, pre-selected in the
	 * Project field. The user can still change or clear it.
	 */
	defaultProjectId?: string | null;
}

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 50;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const Backdrop = styled.div`
	position: absolute;
	inset: 0;
	background: rgb(0 0 0 / 0.4);
`;

const Dialog = styled.div`
	position: relative;
	z-index: 10;
	width: 100%;
	max-width: 28rem;
	border: 1px solid ${p => p.theme.sys.color.border.decorative};
	border-radius: 12px;
	background: #ffffff;
	padding: 24px;
	${raisedSurfaceShadow}
`;

const Header = styled.div`
	margin-bottom: 16px;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const Title = styled.h2`
	margin: 0;
	font-size: 18px;
	line-height: 28px;
	font-weight: 500;
	color: #0f172a;
`;

const CloseButton = styled.button`
	border: 0;
	border-radius: 4px;
	background: transparent;
	padding: 4px;
	color: #94a3b8;
	cursor: pointer;
	&:hover {
		background: #f1f5f9;
		color: #475569;
	}
	&:focus-visible {
		outline: 2px solid ${p => p.theme.sys.color.brand.primary['1']};
		outline-offset: 2px;
	}
`;

const CloseIcon = styled.svg`
	height: 20px;
	width: 20px;
`;

const ErrorBox = styled.div`
	margin-bottom: 16px;
	border-radius: 4px;
	border: 1px solid #fecaca;
	background: #fef2f2;
	padding: 12px 16px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #b91c1c;
`;

const Form = styled.form`
	> * + * {
		margin-top: 20px;
	}
`;

const FieldLabel = styled.label`
	display: block;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: #64748b;
`;

const FieldLabelSpan = styled.span`
	display: block;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: #64748b;
`;

const fieldStyles = `
	margin-top: 4px;
	width: 100%;
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	padding: 8px 12px;
`;

const TextInput = styled.input`
	${fieldStyles}
	${p => p.theme.sys.font?.subheading?.['2']}
	&:focus {
		outline: 2px solid ${p => p.theme.sys.color.brand.primary['1']};
		outline-offset: 2px;
		border-color: ${p => p.theme.sys.color.brand.primary['1']};
		box-shadow: none;
	}
`;

const TextArea = styled.textarea`
	${fieldStyles}
	${p => p.theme.sys.font?.subheading?.['2']}
	&:focus {
		outline: 2px solid ${p => p.theme.sys.color.brand.primary['1']};
		outline-offset: 2px;
		border-color: ${p => p.theme.sys.color.brand.primary['1']};
		box-shadow: none;
	}
`;

const Select = styled.select`
	${fieldStyles}
	${p => p.theme.sys.font?.subheading?.['2']}
	&:focus {
		outline: 2px solid ${p => p.theme.sys.color.brand.primary['1']};
		outline-offset: 2px;
		border-color: ${p => p.theme.sys.color.brand.primary['1']};
		box-shadow: none;
	}
`;

const PickerSlot = styled.div`
	margin-top: 4px;
`;

const LabelColumn = styled.div`
	margin-top: 4px;
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

const LabelChips = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: #475569;
`;

const LabelChip = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	border-radius: 9999px;
	background: #f1f5f9;
	padding: 4px 8px;
`;

const RemoveLabelButton = styled.button`
	border: 0;
	background: transparent;
	padding: 0;
	color: #94a3b8;
	cursor: pointer;
	&:hover {
		color: #475569;
	}
`;

const LoadingText = styled.span`
	${p => p.theme.sys.font?.detail?.['1']}
	color: #94a3b8;
`;

const Actions = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 12px;
`;

export function CreateTicketModal({open, onClose, onCreated, defaultProjectId}: CreateTicketModalProps) {
	const {create, loading, error: serverError} = useCreateTicket();

	const [subject, setSubject] = useState('');
	const [description, setDescription] = useState('');
	const [priority, setPriority] = useState<TicketPriority>('p2');
	const [requesterEmail, setRequesterEmail] = useState('');
	const [assigneeId, setAssigneeId] = useState<string | null>(null);
	const [labels, setLabels] = useState<Array<{id: string; name: string}>>([]);
	const [projectId, setProjectId] = useState<string | null>(null);
	const [validationError, setValidationError] = useState('');
	const {users} = useMentionUsers();

	const subjectRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (open) subjectRef.current?.focus();
	}, [open]);

	// Each time the modal opens, start from the project in context (none when opened
	// from the queue). The user can still change or clear it before creating.
	useEffect(() => {
		if (open) setProjectId(defaultProjectId ?? null);
	}, [open, defaultProjectId]);

	if (!open) return null;

	function resetForm() {
		setSubject('');
		setDescription('');
		setPriority('p2');
		setRequesterEmail('');
		setAssigneeId(null);
		setLabels([]);
		setProjectId(null);
		setValidationError('');
	}

	function handleClose() {
		resetForm();
		onClose();
	}

	// Two entry points reach this: the form's onSubmit (Enter inside a field — HTML
	// implicit submission) and the design-system footer Button. Keep them separate:
	// the Button is React Aria driven and calls onPress with a synthetic PressEvent
	// that has NO preventDefault, so only the form path may touch the event.
	function handleFormSubmit(e: React.FormEvent) {
		e.preventDefault();
		void handleSubmit();
	}

	async function handleSubmit() {
		setValidationError('');

		if (!subject.trim()) {
			setValidationError('Subject is required');
			return;
		}

		try {
			const connectionId = ConnectionHandler.getConnectionID('client:root', 'TicketList_tickets');
			const ticket = await create(
				{
					subject: subject.trim(),
					description: description.trim() || undefined,
					priority,
					channel: 'direct',
					requesterEmail: requesterEmail.trim() || undefined,
					assigneeId: assigneeId ?? undefined,
					labels: labels.length > 0 ? labels.map(l => l.id) : undefined,
					projectId: projectId ?? undefined,
				},
				[connectionId]
			);
			resetForm();
			onClose();
			onCreated?.(ticket);
		} catch {
			// error is already set in the hook's state and rendered below
		}
	}

	const displayError = validationError || serverError;

	return (
		<Overlay>
			<Backdrop data-testid="create-ticket-backdrop" onClick={handleClose} />
			<Dialog role="dialog" aria-labelledby="create-ticket-title">
				<Header>
					<Title id="create-ticket-title">New ticket</Title>
					<CloseButton type="button" aria-label="Close" onClick={handleClose}>
						<CloseIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
							<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
						</CloseIcon>
					</CloseButton>
				</Header>

				{displayError && <ErrorBox role="alert">{displayError}</ErrorBox>}

				<Form onSubmit={handleFormSubmit} noValidate>
					<div>
						<FieldLabel htmlFor="subject">Subject</FieldLabel>
						<TextInput
							id="subject"
							ref={subjectRef}
							type="text"
							value={subject}
							onChange={e => setSubject(e.target.value)}
							placeholder="Brief summary of the issue"
						/>
					</div>

					<div>
						<FieldLabel htmlFor="description">Description</FieldLabel>
						<TextArea
							id="description"
							value={description}
							onChange={e => setDescription(e.target.value)}
							placeholder="Detailed description or initial message"
							rows={4}
						/>
					</div>

					<div>
						<FieldLabel htmlFor="priority">Priority</FieldLabel>
						<Select id="priority" value={priority} onChange={e => setPriority(e.target.value as TicketPriority)}>
							<option value="p3">Low</option>
							<option value="p2">Normal</option>
							<option value="p1">High</option>
							<option value="p0">Urgent</option>
						</Select>
					</div>

					<div>
						<FieldLabel htmlFor="requesterEmail">Requester email</FieldLabel>
						<TextInput
							id="requesterEmail"
							type="email"
							value={requesterEmail}
							onChange={e => setRequesterEmail(e.target.value)}
							placeholder="customer@example.com (defaults to you)"
						/>
					</div>

					<div>
						<FieldLabelSpan>Assignee</FieldLabelSpan>
						<PickerSlot>
							<AssigneePicker
								assigneeId={assigneeId}
								users={users}
								disabled={loading}
								onChange={userId => setAssigneeId(userId)}
							/>
						</PickerSlot>
					</div>

					<div>
						<ProjectDropdown
							label="Project"
							noneLabel="No project"
							value={projectId}
							onChange={setProjectId}
							disabled={loading}
							data-cy="create-ticket-project"
						/>
					</div>

					<div>
						<FieldLabelSpan>Labels</FieldLabelSpan>
						<LabelColumn>
							{labels.length > 0 && (
								<LabelChips>
									{labels.map(label => (
										<LabelChip key={label.id}>
											{label.name || label.id}
											<RemoveLabelButton
												type="button"
												aria-label={`Remove label ${label.name || label.id}`}
												onClick={() => setLabels(prev => prev.filter(x => x.id !== label.id))}
											>
												×
											</RemoveLabelButton>
										</LabelChip>
									))}
								</LabelChips>
							)}
							<Suspense fallback={<LoadingText>Loading…</LoadingText>}>
								<TicketLabelPicker
									ticketId=""
									currentLabelIds={labels.map(l => l.id)}
									disabled={loading}
									onAdd={(id, name) =>
										setLabels(prev => (prev.some(l => l.id === id) ? prev : [...prev, {id, name}]))
									}
								/>
							</Suspense>
						</LabelColumn>
					</div>

					<Actions>
						<Button emphasis={EMPHASIS.LOW} disabled={loading} onPress={handleClose}>
							Cancel
						</Button>
						<Button emphasis={EMPHASIS.HIGH} disabled={loading} onPress={handleSubmit}>
							{loading ? 'Creating…' : 'Create Ticket'}
						</Button>
					</Actions>
				</Form>
			</Dialog>
		</Overlay>
	);
}
